import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { BusinessPartner, CustomerIdentificationQuery } from '@oe-backend/types';
import { firstValueFrom } from 'rxjs';
import { ECPService } from 'src/app/services/api/ecp/ecp.service';
import { UserDataService } from '../../services/user-data/user-data.service';
import { withLoadingState } from '../loading-state/with-loading-state';
import { ECPState } from '../types/ecp.type';

export const ECPStore = signalStore(
	{ providedIn: 'root' },
	withState<ECPState>({
		soldToStores: [],
		shipToStores: [],
		shipToContacts: [],
		selectedSoldToStoreQuery: null,
	}),
	withLoadingState({
		soldToStores: 'init',
		shipToStores: 'init',
		shipToContacts: 'init',
		selectedSoldToStore: 'init',
	}),
	withMethods((state) => {
		const ecpService = inject(ECPService);
		const userDataService = inject(UserDataService);

		//Const to use it in other methods
		const setSelectedSoldToStore = (storeQuery: CustomerIdentificationQuery | null) => {
			patchState(state, { selectedSoldToStoreQuery: storeQuery });
			userDataService.setSelectedSoldToStore(storeQuery || undefined);
		};

		return {
			setSelectedSoldToStore,
			setSelectedSoldToStoreFromBusinessPartner: (businessPartner: BusinessPartner) => {
				const storeQuery =
					businessPartner && businessPartner.customerNumber
						? { zeissCustomerNumber: businessPartner.customerNumber }
						: null;
				setSelectedSoldToStore(storeQuery);
			},
			fetchSoldToStores: async () => {
				state.loading('soldToStores');
				try {
					const soldToStores = await firstValueFrom(ecpService.fetchSoldToStores());
					patchState(state, { soldToStores });
					state.loaded('soldToStores');
				} catch {
					patchState(state, { soldToStores: [] });
					state.errored('soldToStores');
				}
			},
			fetchShipToStores: async () => {
				state.loading('shipToStores');
				try {
					const shipToStores = await firstValueFrom(ecpService.fetchShipToStores());
					patchState(state, { shipToStores });
					state.loaded('shipToStores');
				} catch {
					patchState(state, { shipToStores: [] });
					state.errored('shipToStores');
				}
			},
			fetchShipToContactPersons: async (shipToStore: number) => {
				state.loading('shipToContacts');
				try {
					const shipToContacts = await firstValueFrom(ecpService.fetchContactPersons(shipToStore));
					patchState(state, { shipToContacts });
					state.loaded('shipToContacts');
				} catch {
					patchState(state, { shipToContacts: [] });
					state.errored('shipToContacts');
				}
			},
			fetchSelectedSoldToStore: async () => {
				state.loading('selectedSoldToStore');

				const stores = state.soldToStores();
				try {
					let selectedStoreQuery = (await userDataService.getSelectedSoldToStore()) || null;

					if (selectedStoreQuery && selectedStoreQuery.zeissCustomerNumber) {
						const validStore = stores.some(
							(store) => store.customerNumber === selectedStoreQuery?.zeissCustomerNumber,
						);
						if (!validStore) {
							selectedStoreQuery = null;
						}
					}

					if (!selectedStoreQuery && stores.length === 1 && stores[0].customerNumber) {
						selectedStoreQuery = { zeissCustomerNumber: stores[0].customerNumber };
					}

					setSelectedSoldToStore(selectedStoreQuery);

					state.loaded('selectedSoldToStore');
				} catch {
					patchState(state, { selectedSoldToStoreQuery: null });
					state.errored('selectedSoldToStore');
				}
			},
		};
	}),
	withComputed((state) => ({
		selectedSoldToStore: computed(() => {
			return state
				.soldToStores()
				.find(
					(store) => store.customerNumber === state.selectedSoldToStoreQuery()?.zeissCustomerNumber,
				);
		}),
	})),
);
